import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ProposalIntegrationHelloSign } from './proposalIntegrations.model';
import {
  initState,
  proposalIntegrationsReducer,
  ProposalIntegrationsState,
} from './proposalIntegrations.reducer';

interface ProposalIntegrationsContextProps {
  proposalIntegrationsHelloSign: ProposalIntegrationHelloSign[];
  isLoading: boolean;
  isLoaded: boolean;
  fetchProposalIntegrations: (organizationId: string) => Promise<void>;
}

const ProposalIntegrationsContext = createContext<
  ProposalIntegrationsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ProposalIntegrationsState>;
}>;

export const ProposalIntegrationsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(proposalIntegrationsReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const proposalIntegrationsHelloSign = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter((integration) => integration.status !== 'DISABLED'),
    [state.entities, state.ids]
  );

  const fetchProposalIntegrations: ProposalIntegrationsContextProps['fetchProposalIntegrations'] =
    useCallback(
      async (organizationId) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return;
        }
        dispatch({ type: 'FETCH_PROPOSAL_INTEGRATIONS' });
        try {
          const integrations = await httpClient.get<
            ProposalIntegrationHelloSign[]
          >(`/organization-integrations/${organizationId}`, {
            params: { provider: 'HelloSign' },
          });
          dispatch({
            type: 'FETCH_PROPOSAL_INTEGRATIONS_SUCCESS',
            payload: { integrations },
          });
        } catch (error) {
          dispatch({
            type: 'FETCH_PROPOSAL_INTEGRATIONS_FAILURE',
            payload: {
              error: `Failed to fetch proposal integrations: ${error}`,
            },
          });
        }
      },
      [httpClient, isOnline]
    );

  return (
    <ProposalIntegrationsContext.Provider
      value={{
        proposalIntegrationsHelloSign,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        fetchProposalIntegrations,
      }}
    >
      {children}
    </ProposalIntegrationsContext.Provider>
  );
};

export const useProposalIntegrations = (): ProposalIntegrationsContextProps => {
  const context = useContext(ProposalIntegrationsContext);
  if (context === undefined) {
    throw new Error(
      'useProposalIntegrations must be used within a ProposalIntegrationsProvider'
    );
  }
  return context;
};
